import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Eye, EyeOff, Copy, CheckCircle, Calendar, Key, Play, Code } from 'lucide-react';
import { ApiKey } from '../../types';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { formatDate as formatDateUtil } from '../../utils/dateUtils';

export const APIKeysManagement: React.FC = () => {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyPermissions, setNewKeyPermissions] = useState<string[]>(['projects:read']);
  const [newKeyExpiry, setNewKeyExpiry] = useState<string>('');
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [showCreatedKey, setShowCreatedKey] = useState(false);
  const [copiedKeyId, setCopiedKeyId] = useState<string | null>(null);
  const [deleteKeyId, setDeleteKeyId] = useState<string | null>(null);
  
  // API Sandbox state
  const [showSandbox, setShowSandbox] = useState(false);
  const [sandboxApiKey, setSandboxApiKey] = useState('');
  const [selectedKeyId, setSelectedKeyId] = useState('');
  const [sandboxEndpoint, setSandboxEndpoint] = useState<'GET' | 'POST' | 'PUT' | 'DELETE'>('GET');
  const [sandboxPath, setSandboxPath] = useState('/projects');
  const [sandboxProjectId, setSandboxProjectId] = useState('');
  const [sandboxBody, setSandboxBody] = useState('{\n  "clientId": "...",\n  "startDate": "2025-01-01"\n}');
  const [sandboxResponse, setSandboxResponse] = useState<any>(null);
  const [sandboxLoading, setSandboxLoading] = useState(false);

  // Update sandbox body based on selected path
  useEffect(() => {
    if (sandboxPath === '/buddy/chat') {
      setSandboxBody('{\n  "message": "What projects are currently in progress?",\n  "context": "Show a summary",\n  "responseFormat": "json"\n}');
    } else if (sandboxPath === '/buddy/query') {
      setSandboxBody('{\n  "resource": "projects",\n  "query": "Show all active projects from last 30 days"\n}');
    } else if (sandboxPath === '/buddy/insights') {
      setSandboxBody('{\n  "timeframe": "last 30 days",\n  "focus": "project performance"\n}');
    } else if (sandboxPath === '/projects') {
      setSandboxBody('{\n  "clientId": "...",\n  "startDate": "2025-01-01"\n}');
    } else if (sandboxPath === '/staff') {
      setSandboxBody('{\n  "firstName": "John",\n  "lastName": "Doe",\n  "email": "john@example.com",\n  "phone": "+1234567890"\n}');
    } else if (sandboxPath.includes('/contacts')) {
      setSandboxBody('{\n  "name": "ABC Company",\n  "email": "contact@abc.com",\n  "phone": "+1234567890"\n}');
    } else if (sandboxPath === '/quotes') {
      setSandboxBody('{\n  "clientId": "...",\n  "lineItems": [\n    {\n      "description": "Service",\n      "quantity": 1,\n      "unitPrice": 100,\n      "taxRate": 10\n    }\n  ]\n}');
    }
  }, [sandboxPath]);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${apiUrl}/api/api-keys`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setApiKeys(data.data);
      } else {
        setError(data.message || 'Failed to fetch API keys');
      }
    } catch (err) {
      console.error('Fetch API keys error:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const fetchApiKeysWithRawKey = async (newKeyId: string, rawKey: string) => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${apiUrl}/api/api-keys`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        // Inject rawKey into the newly created key
        const updatedKeys = data.data.map((key: ApiKey) => 
          key._id === newKeyId ? { ...key, rawKey } : key
        );
        return updatedKeys;
      }
      return null;
    } catch (err) {
      console.error('Fetch API keys with rawKey error:', err);
      return null;
    }
  };

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) {
      setError('API key name is required');
      return;
    }

    try {
      setError(null);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${apiUrl}/api/api-keys`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newKeyName,
          permissions: newKeyPermissions,
          expiresAt: newKeyExpiry || undefined
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setCreatedKey(data.data.rawKey);
        setShowCreatedKey(true);
        setSuccess(data.message || 'API key created successfully');
        setNewKeyName('');
        setNewKeyPermissions(['projects:read']);
        setNewKeyExpiry('');
        setShowCreateModal(false);
        
        // Auto-select the newly created key in sandbox
        setSelectedKeyId(data.data._id);
        setSandboxApiKey(data.data.rawKey);
        
        // Fetch updated list and inject rawKey for the new key
        const updatedKeys = await fetchApiKeysWithRawKey(data.data._id, data.data.rawKey);
        if (updatedKeys) {
          setApiKeys(updatedKeys);
        }
      } else {
        setError(data.message || 'Failed to create API key');
      }
    } catch (err) {
      console.error('Create API key error:', err);
      setError('Failed to connect to server');
    }
  };

  const handleDeleteKey = async (keyId: string) => {
    try {
      setError(null);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${apiUrl}/api/api-keys/${keyId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess('API key deleted successfully');
        fetchApiKeys();
      } else {
        setError(data.message || 'Failed to delete API key');
      }
    } catch (err) {
      console.error('Delete API key error:', err);
      setError('Failed to connect to server');
    } finally {
      setDeleteKeyId(null);
    }
  };

  const handleCopyKey = (key: string, keyId: string) => {
    navigator.clipboard.writeText(key);
    setCopiedKeyId(keyId);
    setTimeout(() => setCopiedKeyId(null), 2000);
  };

  const handleTogglePermission = (permission: string) => {
    if (newKeyPermissions.includes(permission)) {
      setNewKeyPermissions(newKeyPermissions.filter(p => p !== permission));
    } else {
      setNewKeyPermissions([...newKeyPermissions, permission]);
    }
  };

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return 'Never';
    return formatDateUtil(date);
  };

  const isExpired = (expiresAt: Date | string | undefined) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const handleTestApiKey = async () => {
    if (!sandboxApiKey) {
      setError('Please enter an API key to test');
      return;
    }

    setSandboxLoading(true);
    setSandboxResponse(null);
    setError(null);

    try {
      let url = `${apiUrl}/api/external/v1${sandboxPath}`;
      
      // Determine the actual HTTP method to use
      let actualMethod = sandboxEndpoint;
      if (sandboxPath.startsWith('/buddy/')) {
        // Buddy AI endpoints have specific methods
        if (sandboxPath === '/buddy/capabilities') {
          actualMethod = 'GET';
        } else {
          actualMethod = 'POST'; // chat, query, insights use POST
        }
      }
      
      // Append resource ID for single-resource endpoints (not for buddy)
      if (sandboxProjectId && !sandboxPath.startsWith('/buddy') && (actualMethod === 'GET' || actualMethod === 'PUT' || actualMethod === 'DELETE')) {
        url += `/${sandboxProjectId}`;
      }

      const options: RequestInit = {
        method: actualMethod,
        headers: {
          'X-API-Key': sandboxApiKey,
          'Content-Type': 'application/json'
        }
      };

      // Add body for POST and PUT requests, or for Buddy AI endpoints
      if (((actualMethod === 'POST' || actualMethod === 'PUT') && sandboxBody) || (sandboxPath.startsWith('/buddy') && sandboxPath !== '/buddy/capabilities')) {
        try {
          options.body = JSON.stringify(JSON.parse(sandboxBody));
        } catch (e) {
          setError('Invalid JSON in request body');
          setSandboxLoading(false);
          return;
        }
      }

      const startTime = Date.now();
      const response = await fetch(url, options);
      const endTime = Date.now();
      const data = await response.json();

      setSandboxResponse({
        status: response.status,
        statusText: response.statusText,
        duration: endTime - startTime,
        data: data,
        url: url,
        method: actualMethod
      });

      if (response.ok) {
        setSuccess('API request successful');
      }
    } catch (err: any) {
      setSandboxResponse({
        status: 0,
        statusText: 'Network Error',
        duration: 0,
        data: { error: err.message },
        url: '',
        method: sandboxEndpoint
      });
      setError(`Request failed: ${err.message}`);
    } finally {
      setSandboxLoading(false);
    }
  };

  const getSandboxCurlCommand = () => {
    let url = `${apiUrl}/api/external/v1${sandboxPath}`;
    if (sandboxProjectId && (sandboxEndpoint === 'GET' || sandboxEndpoint === 'PUT' || sandboxEndpoint === 'DELETE')) {
      url += `/${sandboxProjectId}`;
    }

    let cmd = `curl -X ${sandboxEndpoint} ${url} \\\n  -H "X-API-Key: ${sandboxApiKey || 'YOUR_API_KEY'}"`;
    
    if ((sandboxEndpoint === 'POST' || sandboxEndpoint === 'PUT') && sandboxBody) {
      cmd += ` \\\n  -H "Content-Type: application/json" \\\n  -d '${sandboxBody.replace(/\n/g, '')}'`;
    }
    
    return cmd;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">API Keys</h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Manage API keys for external access to your Projects data
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 dark:bg-accent-500 dark:hover:bg-accent-600"
        >
          <Plus className="w-4 h-4" />
          <span>Create API Key</span>
        </button>
      </div>

      {/* Alert Messages */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg dark:bg-gray-700 dark:border dark:border-red-800/50 dark:border-red-800 dark:text-red-300">
          {error}
        </div>
      )}
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg dark:bg-gray-700 dark:border dark:border-green-800/50 dark:border-green-800 dark:text-green-300">
          {success}
        </div>
      )}

      {/* Created Key Display */}
      {showCreatedKey && createdKey && (
        <div className="p-4 bg-yellow-50 border-2 border-yellow-400 rounded-lg dark:bg-gray-700 dark:border dark:border-warning-800/50 dark:border-yellow-700">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-warning-900 dark:text-warning-100 mb-2">
                ⚠️ Save Your API Key - Shown Only Once
              </h3>
              <p className="text-sm text-warning-700 dark:text-warning-300 mb-3">
                This key cannot be retrieved again. Copy it now and store it securely, or test it immediately in the sandbox below.
              </p>
              <div className="space-y-2">
                <div className="flex items-center space-x-2 p-3 bg-white dark:bg-gray-700 border border-yellow-300 dark:border-yellow-700 rounded font-mono text-sm">
                  <span className="flex-1 break-all text-gray-900 dark:text-gray-100">{createdKey}</span>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(createdKey);
                      setSuccess('Full API key copied to clipboard');
                    }}
                    className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-warning-600 text-white rounded-lg hover:bg-warning-700 dark:bg-warning-700 dark:hover:bg-warning-600"
                  >
                    <Copy className="w-4 h-4" />
                    <span>Copy Full Key</span>
                  </button>
                  <button
                    onClick={() => {
                      setSandboxApiKey(createdKey);
                      setShowSandbox(true);
                      setSuccess('API key loaded in sandbox - ready to test!');
                    }}
                    className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600"
                  >
                    <Play className="w-4 h-4" />
                    <span>Test in Sandbox</span>
                  </button>
                </div>
              </div>
            </div>
            <button
              onClick={() => {
                setShowCreatedKey(false);
                setCreatedKey(null);
              }}
              className="ml-4 text-warning-700 hover:text-warning-900 dark:text-warning-300 dark:hover:text-warning-100"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* API Keys List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-600">
        {apiKeys.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            <Key className="w-12 h-12 mx-auto mb-3 text-gray-400 dark:text-gray-500" />
            <p>No API keys created yet</p>
            <p className="text-sm mt-1">Create your first API key to get started</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Key
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Permissions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Last Used
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Expires
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                {apiKeys.map((key) => (
                  <tr key={key._id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{key.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        Created by {key.createdBy.firstName} {key.createdBy.lastName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <code className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-2 py-1 rounded">
                          {key.prefix}...
                        </code>
                        <button
                          onClick={() => handleCopyKey(key.prefix, key._id)}
                          className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                        >
                          {copiedKeyId === key._id ? (
                            <CheckCircle className="w-4 h-4 text-success-600 dark:text-success-400" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {key.permissions.map((perm) => (
                          <span
                            key={perm}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-accent-100 text-accent-800 dark:bg-accent-900/30 dark:text-accent-300"
                          >
                            {perm}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(key.lastUsed)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {isExpired(key.expiresAt) ? (
                        <span className="text-error-600 dark:text-error-400 font-medium">Expired</span>
                      ) : (
                        formatDate(key.expiresAt)
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          key.isActive && !isExpired(key.expiresAt)
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {key.isActive && !isExpired(key.expiresAt) ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => setDeleteKeyId(key._id)}
                        className="text-error-600 hover:text-error-900 dark:text-error-400 dark:hover:text-error-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* API Documentation */}
      <div className="bg-accent-50 dark:bg-gray-700 dark:border dark:border-accent-800/50 border border-accent-200 dark:border-accent-800 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-accent-900 dark:text-accent-100 mb-3">API Documentation</h3>
        <div className="space-y-3 text-sm text-accent-800 dark:text-accent-200">
          <div>
            <strong>Base URL:</strong> <code className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-2 py-1 rounded">{apiUrl}/api/external/v1</code>
          </div>
          <div>
            <strong>Authentication:</strong> Include your API key in the <code className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-2 py-1 rounded">X-API-Key</code> header
          </div>
          <div className="mt-4">
            <strong>Available Resources:</strong>
            <div className="mt-2 space-y-2 ml-2">
              <div>
                <div className="font-semibold">Projects, Staff, Contacts, Quotes</div>
                <div className="ml-2 space-y-0.5 text-xs">
                  <div>• <code>GET /:resource</code> - List all</div>
                  <div>• <code>GET /:resource/:id</code> - Get single</div>
                  <div>• <code>POST /:resource</code> - Create new</div>
                  <div>• <code>PUT /:resource/:id</code> - Update</div>
                  <div>• <code>DELETE /:resource/:id</code> - Delete</div>
                </div>
              </div>
              <div>
                <div className="font-semibold">Invoices & Bills</div>
                <div className="ml-2 space-y-0.5 text-xs">
                  <div>• <code>GET /:resource</code> - List all</div>
                  <div>• <code>GET /:resource/:id</code> - Get single</div>
                  <div>• <code>POST /:resource</code> - Create new</div>
                  <div>• <code>PUT /:resource/:id</code> - Update</div>
                  <div>• <code>DELETE /:resource/:id</code> - Delete</div>
                </div>
              </div>
              <div>
                <div className="font-semibold">Buddy AI (Agent-to-Agent)</div>
                <div className="ml-2 space-y-0.5 text-xs">
                  <div>• <code>POST /buddy/chat</code> - Chat with AI (read-only queries)</div>
                  <div>• <code>GET /buddy/capabilities</code> - Get AI capabilities</div>
                  <div>• <code>POST /buddy/query</code> - Natural language data queries</div>
                  <div>• <code>POST /buddy/insights</code> - Business insights</div>
                  <div className="text-gray-600 dark:text-gray-400">Note: Write permission enables AI to perform actions</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* API Sandbox */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
        <button
          onClick={() => setShowSandbox(!showSandbox)}
          className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          <div className="flex items-center space-x-3">
            <Code className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">API Sandbox</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Test your API keys directly from the browser</p>
            </div>
          </div>
          <svg
            className={`w-5 h-5 text-gray-500 transition-transform ${showSandbox ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {showSandbox && (
          <div className="p-6 border-t border-gray-200 dark:border-gray-600 space-y-6">
            {/* API Key Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select API Key *
              </label>
              <select
                value={selectedKeyId}
                onChange={(e) => {
                  const keyId = e.target.value;
                  setSelectedKeyId(keyId);
                  const key = apiKeys.find(k => k._id === keyId);
                  if (key?.rawKey) {
                    setSandboxApiKey(key.rawKey);
                    setSuccess('✅ API key loaded - ready to test!');
                  } else {
                    setSandboxApiKey('');
                    setError('API key not available. Only newly created keys can be tested.');
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">-- Select an API Key --</option>
                {apiKeys.filter(k => k.isActive && !isExpired(k.expiresAt)).map((key) => (
                  <option key={key._id} value={key._id}>
                    {key.name}
                  </option>
                ))}
              </select>
              
              {sandboxApiKey && (
                <>
                  <div className="mt-2 p-3 bg-green-50 dark:bg-gray-700 dark:border dark:border-green-800/50 border border-green-200 dark:border-green-800 rounded-lg">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-green-700 dark:text-green-300">
                        ✓ API Key loaded: <code className="bg-white dark:bg-gray-700 px-1 rounded">{sandboxApiKey.substring(0, 20)}...</code>
                      </p>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(sandboxApiKey);
                          setSuccess('Full API key copied to clipboard');
                        }}
                        className="text-green-700 hover:text-success-900 dark:text-green-300 dark:hover:text-success-100"
                        title="Copy full API key"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* cURL Command Preview */}
                  <div className="mt-3">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                        Equivalent cURL Command
                      </label>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(getSandboxCurlCommand());
                          setSuccess('cURL command copied to clipboard');
                        }}
                        className="text-xs text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 flex items-center space-x-1"
                      >
                        <Copy className="w-3 h-3" />
                        <span>Copy</span>
                      </button>
                    </div>
                    <pre className="p-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-xs text-gray-900 dark:text-gray-100 overflow-x-auto">
{getSandboxCurlCommand()}
                    </pre>
                  </div>
                </>
              )}
            </div>

            {/* Endpoint Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  HTTP Method
                </label>
                <select
                  value={sandboxEndpoint}
                  onChange={(e) => setSandboxEndpoint(e.target.value as any)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="DELETE">DELETE</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Resource
                </label>
                <select
                  value={sandboxPath}
                  onChange={(e) => setSandboxPath(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="/projects">Projects</option>
                  <option value="/staff">Staff</option>
                  <option value="/contacts">Contacts (All)</option>
                  <option value="/contacts/clients">Contacts (Clients)</option>
                  <option value="/contacts/suppliers">Contacts (Suppliers)</option>
                  <option value="/quotes">Quotes</option>
                  <option value="/invoices">Invoices</option>
                  <option value="/bills">Bills</option>
                  <option value="/buddy/chat">Buddy AI - Chat</option>
                  <option value="/buddy/capabilities">Buddy AI - Capabilities</option>
                  <option value="/buddy/query">Buddy AI - Query Data</option>
                  <option value="/buddy/insights">Buddy AI - Insights</option>
                </select>
              </div>
            </div>

            {/* Resource ID for single resource operations */}
            {(sandboxEndpoint === 'GET' || sandboxEndpoint === 'PUT' || sandboxEndpoint === 'DELETE') && !sandboxPath.startsWith('/buddy') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Resource ID (optional - for single resource operations)
                </label>
                <input
                  type="text"
                  value={sandboxProjectId}
                  onChange={(e) => setSandboxProjectId(e.target.value)}
                  placeholder={`Leave empty to list all ${sandboxPath.replace('/', '')}s`}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            )}

            {/* Request Body for POST/PUT or Buddy AI */}
            {((sandboxEndpoint === 'POST' || sandboxEndpoint === 'PUT') || sandboxPath.startsWith('/buddy')) && sandboxPath !== '/buddy/capabilities' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Request Body (JSON)
                </label>
                <textarea
                  value={sandboxBody}
                  onChange={(e) => setSandboxBody(e.target.value)}
                  rows={sandboxPath.startsWith('/buddy') ? 8 : 6}
                  placeholder={sandboxPath === '/buddy/chat' ? '{\n  "message": "What projects are in progress?",\n  "context": "Show summary of active projects",\n  "responseFormat": "json"\n}' : ''}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-sm"
                />
              </div>
            )}

            {/* Send Request Button */}
            <button
              onClick={handleTestApiKey}
              disabled={sandboxLoading || !sandboxApiKey}
              className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {sandboxLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Testing...</span>
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  <span>Send Request</span>
                </>
              )}
            </button>

            {/* Response */}
            {sandboxResponse && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Response
                  </label>
                  <div className="flex items-center space-x-3 text-sm">
                    <span className={`px-2 py-1 rounded ${
                      sandboxResponse.status >= 200 && sandboxResponse.status < 300
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                        : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                    }`}>
                      {sandboxResponse.status} {sandboxResponse.statusText}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400">
                      {sandboxResponse.duration}ms
                    </span>
                  </div>
                </div>
                <pre className="p-4 bg-gray-900 dark:bg-black text-success-400 rounded-lg overflow-x-auto text-xs max-h-96">
                  {JSON.stringify(sandboxResponse.data, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create API Key Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 border border-gray-200 dark:border-gray-600">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Create API Key</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Key Name *
                </label>
                <input
                  type="text"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="e.g., Production Integration"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Permissions
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={(() => {
                        const allPerms = [
                          'projects:read', 'projects:write', 'projects:delete',
                          'staff:read', 'staff:write', 'staff:delete',
                          'contacts:read', 'contacts:write', 'contacts:delete',
                          'quotes:read', 'quotes:write', 'quotes:delete',
                          'invoices:read', 'invoices:write', 'invoices:delete',
                          'bills:read', 'bills:write', 'bills:delete',
                          'buddy:read', 'buddy:write'
                        ];
                        return allPerms.every(p => newKeyPermissions.includes(p));
                      })()}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNewKeyPermissions([
                            'projects:read', 'projects:write', 'projects:delete',
                            'staff:read', 'staff:write', 'staff:delete',
                            'contacts:read', 'contacts:write', 'contacts:delete',
                            'quotes:read', 'quotes:write', 'quotes:delete',
                            'invoices:read', 'invoices:write', 'invoices:delete',
                            'bills:read', 'bills:write', 'bills:delete',
                            'buddy:read', 'buddy:write'
                          ]);
                        } else {
                          setNewKeyPermissions([]);
                        }
                      }}
                      className="rounded border-gray-300 dark:border-gray-600 text-accent-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm font-medium text-accent-600 dark:text-accent-400">Select All</span>
                  </label>
                </div>
                <div className="space-y-3 max-h-64 overflow-y-auto p-2 border border-gray-200 dark:border-gray-600 rounded-lg">
                  {[
                    { group: 'Projects', perms: ['projects:read', 'projects:write', 'projects:delete'] },
                    { group: 'Staff', perms: ['staff:read', 'staff:write', 'staff:delete'] },
                    { group: 'Contacts', perms: ['contacts:read', 'contacts:write', 'contacts:delete'] },
                    { group: 'Quotes', perms: ['quotes:read', 'quotes:write', 'quotes:delete'] },
                    { group: 'Invoices', perms: ['invoices:read', 'invoices:write', 'invoices:delete'] },
                    { group: 'Bills', perms: ['bills:read', 'bills:write', 'bills:delete'] },
                    { group: 'Buddy AI', perms: ['buddy:read', 'buddy:write'] }
                  ].map((group) => (
                    <div key={group.group} className="space-y-1">
                      <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{group.group}</div>
                      {group.perms.map((perm) => (
                        <label key={perm} className="flex items-center ml-2">
                          <input
                            type="checkbox"
                            checked={newKeyPermissions.includes(perm)}
                            onChange={() => handleTogglePermission(perm)}
                            className="rounded border-gray-300 dark:border-gray-600 text-accent-600 focus:ring-blue-500"
                          />
                          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{perm}</span>
                        </label>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Expiration Date (Optional)
                </label>
                <input
                  type="datetime-local"
                  value={newKeyExpiry}
                  onChange={(e) => setNewKeyExpiry(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewKeyName('');
                  setNewKeyPermissions(['projects:read']);
                  setNewKeyExpiry('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateKey}
                className="flex-1 px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 dark:bg-accent-500 dark:hover:bg-accent-600"
              >
                Create Key
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteKeyId && (
        <ConfirmDialog
          isOpen={true}
          title="Delete API Key"
          message="Are you sure you want to delete this API key? Any applications using this key will immediately lose access."
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={() => handleDeleteKey(deleteKeyId)}
          onCancel={() => setDeleteKeyId(null)}
          type="danger"
        />
      )}
    </div>
  );
};

